import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { IItemForGetShortInfo, IGetSubCategoriesShort } from 'types';
import { useFetchData } from 'hooks/useFetchData';
import MultipleSelect from './components/MultipleSelect';
import SelectedItem from 'components/SelectedItem/SelectedItem';
import { getSubCategoriesShortInfo } from 'backend/subcategories';
import style from './SubcategoryMultipleSelect.module.scss';

interface ISubcategoryMultipleSelect {
	setIDsSubcategories: (value: string[]) => void;
	setFetchedSubcategories: (value: IItemForGetShortInfo[]) => void;
	updateSubcategories: (value: IItemForGetShortInfo[]) => void;
	changeLoaderStatus: (value: boolean) => void;
	IDsSelectedCategories: string[];
	initialIDsSelectedSubcategories: string[];
	IDsSelectedSubcategories: string[];
}

const SubcategoryMultipleSelect: React.FC<ISubcategoryMultipleSelect> = ({
	setIDsSubcategories,
	setFetchedSubcategories,
	updateSubcategories,
	changeLoaderStatus,
	IDsSelectedCategories,
	initialIDsSelectedSubcategories,
	IDsSelectedSubcategories,
}) => {
	const { response, loading, refetch, responseError } = useFetchData<IGetSubCategoriesShort, string[]>(
		IDsSelectedCategories.length ? getSubCategoriesShortInfo : null,
		IDsSelectedCategories,
	);
	const [isFirstRender, setIsFirstRender] = useState<boolean>(true);

	const handleRemove = useCallback(
		(id: string) => {
			const updatedSelectedItems = IDsSelectedSubcategories.filter((el) => el !== id);
			setIDsSubcategories(updatedSelectedItems);
		},
		[IDsSelectedSubcategories, setIDsSubcategories],
	);

	const subcategoriesList = useMemo(() => {
		return IDsSelectedSubcategories.map((el) => {
			const label = response?.data.subCategories.find((item) => item.id === el)?.name || '';
			return <SelectedItem key={el} label={label} onDelete={() => handleRemove(el)} />;
		});
	}, [IDsSelectedSubcategories, response, handleRemove]);

	useEffect(() => {
		if (IDsSelectedCategories.length) {
			refetch();
		} else {
			setIDsSubcategories([]);
		}
	}, [IDsSelectedCategories.length]);

	useEffect(() => {
		if (response) {
			setFetchedSubcategories(response.data.subCategories);
			updateSubcategories(response.data.subCategories);
		}
		if (isFirstRender && response) {
			setIDsSubcategories(initialIDsSelectedSubcategories);
			setIsFirstRender(false);
			!initialIDsSelectedSubcategories.length && changeLoaderStatus(false);
		}
	}, [response, isFirstRender, initialIDsSelectedSubcategories]);

	return (
		<div className={style.container} data-test="SubcategoryMultipleSelect">
			<div className={style.field}>
				<MultipleSelect
					data={response?.data.subCategories || []}
					isLoading={loading}
					refetch={refetch}
					responseError={responseError}
					IDsSelectedCategories={IDsSelectedCategories}
					IDsSelectedSubcategories={IDsSelectedSubcategories}
					setIDsSubcategories={setIDsSubcategories}
				/>
			</div>
			<div className={style.groupsList}>{subcategoriesList}</div>
		</div>
	);
};

export default React.memo(SubcategoryMultipleSelect);
